import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Snackbar, Tooltip } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { colors, onCopyText, openInNewTab } from "common";
import BazarButton from "components/BazarButton";
import { FlexBox } from "components/flex-box";
import { H3, H5, Paragraph, Small, Tiny } from "components/Typography";
import useWindowSize from "hooks/useWindowSize";
import { Box } from "@mui/system";
import ClipboardJS from "clipboard";
import { CreateOrderResponse } from "modules/cart/types";
import { useLinkHandler } from "hooks";
import LazyImage from "components/LazyImage";
import { useRouter } from "next/router";
import { buyNowPayLaterMsg } from "common/constants/payment";

type Props = {
  isVisible: boolean;
  orderRefNo: string;
  onClickClose: () => void;
  orderResponse?: CreateOrderResponse;
  paymentLink?: string;
};

export const OrderPlacedDialog: FC<Props> = ({
  isVisible,
  onClickClose,
  orderResponse,
  orderRefNo = "",
  paymentLink,
}) => {
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const { openFbMessengerWithMessage } = useLinkHandler();
  const router = useRouter();

  const orderCopyMessageTxt = useMemo(() => {
    return `Hello PC Worth. I ordered from your website, my Order Reference No. is ${orderRefNo}.`;
  }, [orderRefNo]);

  const onHandleCopyText = async () => {
    onCopyText(orderCopyMessageTxt);
    setOpenSnackBar(true);
  };

  useEffect(() => {
    const clipboardIns = new ClipboardJS(".copyClass");
    return () => {
      clipboardIns.destroy();
    };
  }, []);

  return (
    <Dialog open={isVisible} fullWidth={true} maxWidth="md">
      <DialogContent>
        <FlexBox flexDirection="column" mt={1}>
          <H3 fontSize={18} mb={1} textAlign="center" color={colors.success}>
            ORDER PLACED
          </H3>
          {orderRefNo ? (
            <Box display="inline" textAlign="center">
              <Paragraph>Order Ref No.:</Paragraph>
              <H5 lineHeight={1} ml={0.5}>
                {orderRefNo}
              </H5>
            </Box>
          ) : null}

          <textarea
            style={{ height: 0, width: 0, opacity: 0 }}
            value={orderCopyMessageTxt}
            id="bar"
          />

          <Tooltip followCursor title="Click To Copy">
            <Box
              display={"flex"}
              alignItems="center"
              width={"100%"}
              justifyContent={"center"}
              sx={{ cursor: "pointer" }}
              mt={-1}
              mb={1}
            >
              <Small onClick={onHandleCopyText} color={colors.link}>
                Click To Copy
              </Small>
            </Box>
          </Tooltip>

          <Box sx={{ width: "100%", cursor: "pointer" }}>
            <LazyImage
              width={950}
              height={180}
              objectFit="contain"
              src={"/assets/images/banners/buy-now-paylater.png"}
              onClick={() => openFbMessengerWithMessage(buyNowPayLaterMsg)}
            />
          </Box>

          {orderResponse?.data?.payment_link || paymentLink ? (
            <BazarButton
              variant="outlined"
              sx={{ mt: 2 }}
              color="secondary"
              onClick={() =>
                openInNewTab(orderResponse?.data?.payment_link || paymentLink)
              }
              startIcon={<CreditCardIcon />}
            >
              PAY IN FULL NOW
            </BazarButton>
          ) : (
            <H5 textAlign="center" mt={2}>
              If you chose Cash on Delivery, a representative from PC Worth will
              contact you to confirm your order.
            </H5>
          )}

          <Tiny display="inline" textAlign="center">
            For straight and full payments you can directly process it here.
            This Link will expire in 10 minutes.
          </Tiny>

          <BazarButton
            sx={{ mt: 2 }}
            variant="text"
            color="error"
            onClick={onClickClose}
          >
            <Tiny>CLOSE</Tiny>
          </BazarButton>
        </FlexBox>
      </DialogContent>
      <Snackbar
        open={openSnackBar}
        message="Text Copied"
        autoHideDuration={3000}
        onClose={() => setOpenSnackBar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Dialog>
  );
};
